import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";

import {
  ActionsCell,
  Card,
  CardActions,
  CardBody,
  CardHeader,
  CardTitle,
  CenterMessage,
  Container,
  DeviceTag,
  EmptyState,
  Grid,
  Grid2,
  HeaderBar,
  HeaderLeft,
  HeaderRight,
  InfoItem,
  InfoList,
  Money,
  PdfActions,
  PdfButton,
  StatusChip,
  StatusSelectWrap,
  StepBanner,
  SummaryBar,
  SummaryItem,
  Table,
  TableCell,
  TableRow,
  TableScroll,
  Title,
  TitleBlock
} from "./workorder-detail.styled";

import { FaList, FaRegFilePdf } from "react-icons/fa";
import {
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiCpu,
  FiEdit,
  FiEye,
  FiFileText,
  FiInbox,
  FiInfo,
  FiMail,
  FiPhone,
  FiSmartphone,
  FiTool,
  FiTrash2,
  FiUser,
  FiX
} from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { MdOutlinePlaylistAddCheck } from "react-icons/md";
import { ActionButton } from "./workorder.styled";

import AssignDeviceModal from "../../components/modal/AssignDeviceModal";
import DeviceCreateModal from "../../components/modal/DeviceCreateModal";
import ServiceCreateModal from "../../components/modal/ServiceCreateModal";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { formatPhone } from "../../common/utils/formatters";
import { statusColor } from "../../common/utils/orderStatusColor";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import { TypeOrderStatus, TypeWorkOrderPdf } from "../../components/enum/order";
import {
  FinancialPaymentMethod,
  FinancialPaymentMethodLabel
} from "../../components/enum/financialEnums";
import HelpVideoButton from "../../components/HelpVideoButton";
import AssignProductModal from "../../components/modal/AssignProductModal";
import AssignServiceModal from "../../components/modal/AssignServiceModal";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import { downloadPdfFrom } from "../../services/pdf.service";
import {
  ConditionPhotoInterface,
  deleteConditionPhoto,
  getConditionPhoto,
  uploadConditionPhoto
} from "../../services/workOrderDevicePhoto.service";

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(n || 0);

const TECH_REPORT_STATUS_META: Record<string, { label: string; color: string }> = {
  NOT_STARTED: { label: "Não iniciado", color: "#6b7280" },
  DRAFT: { label: "Rascunho", color: "#f59e0b" },
  COMPLETED: { label: "Concluído", color: "#22c55e" },
  REOPENED: { label: "Reaberto", color: "#3b82f6" }
};

/*
 * WorkOrderDetail – página de detalhe da O.S.
 */
const WorkOrderDetail: React.FC = () => {
  /* ---------------------------- hooks --------------------------- */
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isUser } = useAuth();

  /* ---------------------------- state --------------------------- */
  const [order, setOrder] = useState<any>(null);
  const [technicalReportStatus, setTechnicalReportStatus] = useState<
    "NOT_STARTED" | "DRAFT" | "COMPLETED" | "REOPENED"
  >("NOT_STARTED");
  const [devices, setDevices] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [orderGrandTotal, setOrderGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // filtros
  const [serialFilter, setSerialFilter] = useState("");
  const [clientId, setClientId] = useState<number>();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [conditionPhoto, setConditionPhoto] = useState<ConditionPhotoInterface | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoPromptOpen, setPhotoPromptOpen] = useState(false);
  const [pendingDocType, setPendingDocType] = useState<TypeWorkOrderPdf | null>(null);

  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizeAmount, setFinalizeAmount] = useState("");
  const [finalizePaymentMethod, setFinalizePaymentMethod] = useState<string>(
    FinancialPaymentMethod.PIX
  );
  const [finalizeNotes, setFinalizeNotes] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const [emailingBudget, setEmailingBudget] = useState(false);
  const [serviceSelectToRemove, setServiceSelectToRemove] = useState<any>(null);
  const [productSelectToRemove, setProductSelectToRemove] = useState<any>(null);

  const [serviceSelectToDelete, setServiceSelectToDelete] = useState<any>(null);

  // modals & dialogs
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignServiceModalOpen, setAssignServiceModalOpen] = useState(false);
  const [assignProductModalOpen, setAssignProductModalOpen] = useState(false);

  const [serviceModalOpen, setServiceModalOpen] = useState(false);

  const [confirmRemoveService, setConfirmRemoveService] = useState(false);
  const [confirmRemoveProduct, setConfirmRemoveProduct] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteService, setConfirmDeleteService] = useState(false);

  // entidades em edição / remoção
  const [deviceToEdit, setDeviceToEdit] = useState<any>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<any>(null);
  const [serviceToEdit, setServiceToEdit] = useState<any>(null);

  // status da OS (controlado pelo usuário via <Select>)
  const [status, setStatus] = useState<keyof typeof TypeOrderStatus>("OPENED");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const containerId = "toaster-workorder-detail";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchServices = async (client: number, serial: string) => {
    try {
      const { data } = await api.get("/service-performed/list", {
        params: {
          order_id: id,
          client_id: client,
          ...(serial ? { device_serial_number: serial } : {})
        }
      });

      const flat = data.map((sp: any) => ({
        id: sp.service_performed_id,
        name: sp.service_performed_name,
        description: sp.service_performed_description,
        cost: +sp.service_performed_cost
      }));
      setServices(flat);
    } catch (err) {
      console.error(err);
      setServices([]);
    }
  };

  const fetchProducts = async (
    client: number,
    device_serial_number: string
  ) => {
    try {
      const { data } = await api.get("/products/list", {
        params: {
          order_id: id,
          client_id: client,
          ...(device_serial_number ? { device_serial_number } : {})
        }
      });

      const flat = data.map((p: any) => ({
        id: p.product_id,
        name: p.product_name,
        price: parseFloat(p.product_unit_price),
        quantity: p.workOrderDeviceProducts[0]?.quantity ?? 0
      }));

      setProducts(flat);
    } catch (err) {
      console.error(err);
      setProducts([]);
    }
  };

  // Total geral da OS (todos os equipamentos, independente da seleção) —
  // sempre visível no resumo, sem precisar clicar em "mostrar todos".
  const fetchOrderTotal = async (client: number) => {
    try {
      const [svcRes, prodRes] = await Promise.all([
        api.get("/service-performed/list", {
          params: { order_id: id, client_id: client }
        }),
        api.get("/products/list", {
          params: { order_id: id, client_id: client }
        })
      ]);

      const svcTotal = (svcRes.data as any[]).reduce(
        (acc, sp) => acc + (+sp.service_performed_cost || 0),
        0
      );
      const prodTotal = (prodRes.data as any[]).reduce((acc, p) => {
        const quantity = Number(p.workOrderDeviceProducts?.[0]?.quantity) || 0;
        const price = parseFloat(p.product_unit_price) || 0;
        return acc + quantity * price;
      }, 0);

      setOrderGrandTotal(svcTotal + prodTotal);
    } catch (err) {
      console.error("Erro ao calcular total geral da OS", err);
    }
  };

  // Carregamento inicial
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const [orderRes, devicesRes] = await Promise.all([
          api.get(`/work-order/${id}`),
          api.get(`/work-order/${id}/devices`)
        ]);
        setOrder(orderRes.data);
        setDevices(devicesRes.data);
        setClientId(orderRes.data.client?.client_id);

        if (orderRes.data?.status) {
          setStatus(orderRes.data.status as keyof typeof TypeOrderStatus);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }

      // 404 é esperado quando o relatório ainda não foi criado — não é
      // erro, só significa "não iniciado".
      api
        .get(`/work-order/${id}/technical-report`)
        .then((r) => setTechnicalReportStatus(r.data?.status ?? "NOT_STARTED"))
        .catch(() => setTechnicalReportStatus("NOT_STARTED"));
    })();
  }, [id]);

  // Quando cliente/serial mudam, atualiza listas
  useEffect(() => {
    if (!clientId) return;
    fetchServices(clientId, serialFilter);
    fetchProducts(clientId, serialFilter);
  }, [clientId, serialFilter, id]);

  // Total geral independe do equipamento selecionado — só recalcula quando
  // o cliente/OS muda (as mutações abaixo já disparam isso de novo).
  useEffect(() => {
    if (!clientId) return;
    fetchOrderTotal(clientId);
  }, [clientId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Havendo um único equipamento na OS, já vem selecionado por padrão.
  // Só exige escolha manual quando houver mais de um.
  useEffect(() => {
    if (devices.length === 1 && !selectedDeviceId) {
      const only = devices[0];
      setSelectedDeviceId(String(only.device_id));
      setSerialFilter(only.device_serial_number);
    }
  }, [devices]); // eslint-disable-line react-hooks/exhaustive-deps

  // work_order_device_id (PK de junção) do equipamento selecionado — já vem
  // no GET /work-order/:id (order.devices), sem chamada extra à API.
  const deviceIdToWodId = useMemo(() => {
    const map = new Map<string, number>();
    (order?.devices ?? []).forEach((d: any) => {
      map.set(String(d.device_id), d.work_order_device_id);
    });
    return map;
  }, [order]);

  const selectedWorkOrderDeviceId = selectedDeviceId
    ? deviceIdToWodId.get(String(selectedDeviceId))
    : undefined;

  // Foto de condição do equipamento selecionado
  useEffect(() => {
    if (!id || !selectedWorkOrderDeviceId) {
      setConditionPhoto(null);
      return;
    }
    (async () => {
      try {
        setPhotoLoading(true);
        const data = await getConditionPhoto(id, selectedWorkOrderDeviceId);
        setConditionPhoto(data);
      } catch (err) {
        console.error("Erro ao buscar foto do equipamento", err);
        setConditionPhoto(null);
      } finally {
        setPhotoLoading(false);
      }
    })();
  }, [id, selectedWorkOrderDeviceId]);

  const handlePhotoFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !id || !selectedWorkOrderDeviceId) return;

    try {
      setPhotoUploading(true);
      const data = await uploadConditionPhoto(id, selectedWorkOrderDeviceId, file);
      setConditionPhoto(data);
      autoSuccess("Foto do equipamento salva com sucesso!");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRemoveConditionPhoto = async () => {
    if (!id || !selectedWorkOrderDeviceId) return;
    try {
      setPhotoUploading(true);
      await deleteConditionPhoto(id, selectedWorkOrderDeviceId);
      setConditionPhoto({ hasPhoto: false });
      autoSuccess("Foto removida.");
    } catch (err) {
      handleHttpError(err);
    } finally {
      setPhotoUploading(false);
    }
  };

  // PATCH apenas quando o usuário muda no Select
  const handleChangeStatus = async (next: keyof typeof TypeOrderStatus) => {
    if (!id) return;
    if (next === status) return;

    try {
      setIsUpdatingStatus(true);
      const { data } = await api.patch(`/work-order/${id}/status`, null, {
        params: { status: next }
      });

      setOrder(data);
      setStatus(next);
      autoSuccess("Status atualizado com sucesso!");
    } catch (err: any) {
      autoError(
        `Erro ao atualizar status: ${
          err?.response?.data?.message ?? err?.message ?? "desconhecido"
        }`
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const openDeviceModal = (device?: any) => {
    setDeviceToEdit(device || null);
    setDeviceModalOpen(true);
  };

  // Reatualiza a OS (necessário para obter o work_order_device_id do
  // vínculo recém-criado, usado pela foto de condição do equipamento).
  const refreshOrder = () => {
    api.get(`/work-order/${id}`).then((r) => setOrder(r.data));
  };

  const handleDeviceSaved = () => {
    setDeviceModalOpen(false);
    api.get(`/work-order/${id}/devices`).then((r) => setDevices(r.data));
    refreshOrder();
  };

  const selectDeviceForFilter = (device_id: string, serial: string) => {
    autoInfo(serial ? "Equipamento selecionado." : "Mostrando todos os itens.");
    setSelectedDeviceId(device_id || null);
    setSerialFilter(serial);
  };

  const askDeleteDevice = (device: any) => {
    setDeviceToDelete(device);
    setConfirmDeleteOpen(true);
  };

  const askDeleteService = (service_id: any) => {
    setServiceSelectToDelete(service_id);
    setConfirmDeleteService(true);
  };

  const askRemoveService = (service_id: any) => {
    setServiceSelectToRemove(service_id);
    setConfirmRemoveService(true);
  };

  const askRemoveProduct = (product_id: any) => {
    setProductSelectToRemove(product_id);
    setConfirmRemoveProduct(true);
  };

  const handleConfirmDeleteDevice = async () => {
    if (!deviceToDelete) return;
    try {
      await api.delete(`/devices/${deviceToDelete.device_id}`);
      setDevices((prev) =>
        prev.filter((d) => d.device_id !== deviceToDelete.device_id)
      );
      if (serialFilter === deviceToDelete.device_serial_number) {
        setSerialFilter("");
        setSelectedDeviceId(null);
      }
      autoWarn("Device removido com sucesso!");
    } catch (err) {
      console.error(err);
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  // Só pergunta "com fotos?" quando algum equipamento da OS realmente tem
  // foto de condição (não expirada) — caso contrário gera direto, sem modal.
  const hasAnyConditionPhoto = (order?.devices ?? []).some(
    (d: any) => d.has_condition_photo
  );

  const downloadWorkOrderDocument = async (
    type: TypeWorkOrderPdf,
    withPhotos: boolean
  ) => {
    const suffix = withPhotos ? "?withPhotos=true" : "";
    try {
      if (type === TypeWorkOrderPdf.WORK_ORDER) {
        await downloadPdfFrom(`/pdf/work-order/${id}/pdf${suffix}`, {
          overrideServerName: true,
          appendTimestamp: true,
          filename: `ordem-servico-${id}.pdf`
        });
      } else {
        await downloadPdfFrom(`/pdf/work-order2/${id}/pdf${suffix}`, {
          overrideServerName: true,
          appendTimestamp: true,
          filename: `ordem-servico-${id}.pdf`
        });
      }
    } catch (err) {
      console.error("Erro ao baixar O.S.", err);
    }
  };

  const handleWorkOrderGetDocument = async (type: TypeWorkOrderPdf) => {
    if (hasAnyConditionPhoto) {
      setPendingDocType(type);
      setPhotoPromptOpen(true);
      return;
    }
    await downloadWorkOrderDocument(type, false);
  };

  const handlePhotoPromptChoice = async (withPhotos: boolean) => {
    setPhotoPromptOpen(false);
    const type = pendingDocType;
    setPendingDocType(null);
    if (type) await downloadWorkOrderDocument(type, withPhotos);
  };

  const handleWorkOrderGetDeviceDocument = async () => {
    try {
      await downloadPdfFrom(`/pdf/work-order/${id}/device-reports`, {
        overrideServerName: true,
        appendTimestamp: true,
        filename: `device-reports-${id}.pdf`
      });
    } catch (err) {
      console.error("Erro ao baixar relatórios de dispositivos", err);
    }
  };

  const handleWorkOrderGetBudgetDocument = async () => {
    try {
      await downloadPdfFrom(`/pdf/work-order/${id}/budget`, {
        overrideServerName: true,
        appendTimestamp: true,
        filename: `orcamento-${id}.pdf`
      });
    } catch (err) {
      console.error("Erro ao baixar orçamento", err);
    }
  };

  const handleWorkOrderEmailBudget = async () => {
    try {
      setEmailingBudget(true);
      const { data } = await api.post(`/pdf/work-order/${id}/budget/email`);
      autoSuccess(`Orçamento enviado por e-mail para ${data.to}.`);
    } catch (err) {
      handleHttpError(err);
    } finally {
      setEmailingBudget(false);
    }
  };

  const openServiceModalCreate = () => {
    setServiceToEdit(null);
    setServiceModalOpen(true);
  };

  const openServiceModalEdit = async (service_id: number) => {
    try {
      const { data } = await api.get(`/service-performed/${service_id}`);
      setServiceToEdit(data);
      setServiceModalOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleServiceSaved = () => {
    setServiceModalOpen(false);
    if (clientId !== undefined) {
      fetchServices(clientId, serialFilter);
      fetchOrderTotal(clientId);
    }
  };

  const handleRemoveService = async () => {
    const service_id = serviceSelectToRemove;

    try {
      await api.delete(
        `/service-performed/${service_id}/order/${id}/device/${selectedDeviceId}`
      );
      setServices((prev) => prev.filter((s) => s.id !== service_id));
      if (clientId !== undefined) fetchOrderTotal(clientId);
      autoSuccess("Serviço removido com sucesso!");
    } catch (err) {
      handleHttpError(err);
      console.error("Erro ao excluir serviço", err);
    } finally {
      setConfirmRemoveService(false);
    }
  };

  const handleDeleteService = async () => {
    const service_id = serviceSelectToDelete;
    try {
      await api.delete(`/service-performed/${service_id}`);
      setServices((prev) => prev.filter((s) => s.id !== service_id));
      if (clientId !== undefined) fetchOrderTotal(clientId);
      autoSuccess("Serviço excluído com sucesso!");
    } catch (err) {
      handleHttpError(err);
      console.error("Erro ao excluir serviço", err);
    } finally {
      setConfirmDeleteService(false);
    }
    setConfirmDeleteService(false);
  };

  const handleRemoveProduct = async () => {
    const product_id = productSelectToRemove;

    if (!selectedDeviceId) {
      autoError("Nenhum equipamento selecionado!");
    } else {
      try {
        await api.delete(
          `/products/${product_id}/order/${id}/device/${selectedDeviceId}`
        );
        setProducts((prev) => prev.filter((s) => s.id !== product_id));
        if (clientId !== undefined) fetchOrderTotal(clientId);
        autoSuccess("Produto removido com sucesso!");
      } catch (err: any) {
        autoError(
          `Erro: ${err.response?.data?.message || "Erro ao excluir produto"}`
        );
      } finally {
        setConfirmRemoveProduct(false);
      }
    }

    setConfirmRemoveProduct(false);
  };

  const totalServicesValue = services.reduce(
    (acc, s) => acc + (s.cost || 0),
    0
  );
  const totalPartsValue = products.reduce((acc: number, p: any) => {
    const quantity = Number(p.quantity) || 0;
    const price = Number(p.price) || 0;
    return acc + quantity * price;
  }, 0);

  const totalValue = totalServicesValue + totalPartsValue;

  const selectedDevice = devices.find(
    (d) => String(d.device_id) === String(selectedDeviceId)
  );
  const noDevices = devices.length === 0;
  const hasSelection = Boolean(selectedDeviceId);

  const amountReceived = Number(order?.amount_received) || 0;
  const remainingBalance = Math.max(orderGrandTotal - amountReceived, 0);
  const isOrderCompleted = order?.status === "COMPLETED";

  const openFinalizeModal = () => {
    setFinalizeAmount(remainingBalance > 0 ? remainingBalance.toFixed(2) : "0");
    setFinalizePaymentMethod(FinancialPaymentMethod.PIX);
    setFinalizeNotes("");
    setFinalizeModalOpen(true);
  };

  const handleFinalizeOrder = async () => {
    const amountNum = Number(String(finalizeAmount).replace(",", "."));
    if (Number.isNaN(amountNum) || amountNum < 0) {
      autoError("Valor inválido.");
      return;
    }
    if (amountNum > remainingBalance + 0.01) {
      autoError(
        `Valor não pode exceder o saldo restante (${brl(remainingBalance)}).`
      );
      return;
    }

    try {
      setFinalizing(true);
      let receiptId: number | null = null;

      if (amountNum > 0) {
        const { data } = await api.post(`/work-order/${id}/receipts`, {
          amount: amountNum,
          payment_method: finalizePaymentMethod,
          notes: finalizeNotes || undefined
        });
        receiptId = data?.receipt?.work_order_receipt_id ?? null;
      }

      const { data: updatedOrder } = await api.patch(
        `/work-order/${id}/status`,
        null,
        { params: { status: "COMPLETED" } }
      );
      setOrder(updatedOrder);
      setStatus("COMPLETED");
      setFinalizeModalOpen(false);
      autoSuccess("O.S. finalizada com sucesso!");

      if (receiptId) {
        await downloadPdfFrom(`/pdf/work-order/${id}/receipts/${receiptId}`, {
          overrideServerName: true,
          appendTimestamp: true,
          filename: `recibo-os-${id}.pdf`
        });
      }
    } catch (err) {
      handleHttpError(err);
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) return <CenterMessage>Carregando...</CenterMessage>;
  if (!order) return <CenterMessage>Ordem não encontrada.</CenterMessage>;

  return (
    <Container>
      <PageToaster containerId={containerId} />

      {/* ---------------- Cabeçalho ---------------- */}
      <HeaderBar>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/OOr0oS0v4Pw"
          modalTitle="Gerenciar Ordem de Serviço"
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
        />

        <HeaderLeft>
          <TitleBlock>
            <span className="eyebrow">Ordem de Serviço</span>
            <Title># {order.order_number}</Title>
          </TitleBlock>
          <StatusChip $color={statusColor(order.status)}>
            {TypeOrderStatus[order.status as keyof typeof TypeOrderStatus]}
          </StatusChip>
        </HeaderLeft>

        <HeaderRight>
          <StatusSelectWrap>
            <Select
              fullWidth
              size="small"
              value={status}
              disabled={isUpdatingStatus}
              onChange={(e) =>
                handleChangeStatus(
                  e.target.value as keyof typeof TypeOrderStatus
                )
              }
            >
              {Object.entries(TypeOrderStatus)
                .filter(([key]) => key !== "NONE")
                .map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
          </StatusSelectWrap>

          {!isUser && !isOrderCompleted && (
            <CustomTooltip title="Confirmar pagamento, finalizar a OS e gerar o recibo">
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<FiCheckCircle />}
                onClick={openFinalizeModal}
                disabled={noDevices}
              >
                Finalizar O.S.
              </Button>
            </CustomTooltip>
          )}

          <PdfActions>
            <CustomTooltip title="Baixar PDF da Ordem de Serviço">
              <PdfButton
                $color="#ef4444"
                disabled={noDevices}
                onClick={() =>
                  handleWorkOrderGetDocument(TypeWorkOrderPdf.WORK_ORDER)
                }
              >
                <FaRegFilePdf />
                <small>O.S</small>
              </PdfButton>
            </CustomTooltip>

            <CustomTooltip title="Baixar PDF da Ordem de Serviço detalhada">
              <PdfButton
                $color="#22c55e"
                disabled={noDevices}
                onClick={() =>
                  handleWorkOrderGetDocument(
                    TypeWorkOrderPdf.WORK_ORDER_DETAILS
                  )
                }
              >
                <FaRegFilePdf />
                <small>O.S.D</small>
              </PdfButton>
            </CustomTooltip>

            <CustomTooltip title="Baixar PDF dos Equipamentos">
              <PdfButton
                $color="#b91c1c"
                disabled={noDevices}
                onClick={handleWorkOrderGetDeviceDocument}
              >
                <FaRegFilePdf />
                <small>RTI</small>
              </PdfButton>
            </CustomTooltip>

            <CustomTooltip
              title={`Relatório técnico da OS — ${TECH_REPORT_STATUS_META[technicalReportStatus].label}`}
            >
              <div style={{ position: "relative", display: "inline-block" }}>
                <PdfButton
                  $color="#60a5fa"
                  disabled={noDevices}
                  onClick={() => navigate(`/workorder-details/${id}/technical-report`)}
                >
                  <FiFileText />
                  <small>Rel. Técnico</small>
                </PdfButton>
                <span
                  style={{
                    position: "absolute",
                    top: -3,
                    right: -3,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: TECH_REPORT_STATUS_META[technicalReportStatus].color,
                    border: "2px solid var(--surface-2)"
                  }}
                />
              </div>
            </CustomTooltip>

            <CustomTooltip title="Baixar orçamento em PDF">
              <PdfButton
                $color="#a855f7"
                disabled={noDevices}
                onClick={handleWorkOrderGetBudgetDocument}
              >
                <FaRegFilePdf />
                <small>Orçamento</small>
              </PdfButton>
            </CustomTooltip>

            <CustomTooltip title="Enviar orçamento por e-mail para o cliente">
              <PdfButton
                $color="#0ea5e9"
                disabled={noDevices || emailingBudget}
                onClick={handleWorkOrderEmailBudget}
              >
                <FiMail />
                <small>{emailingBudget ? "Enviando..." : "E-mail"}</small>
              </PdfButton>
            </CustomTooltip>
          </PdfActions>
        </HeaderRight>
      </HeaderBar>

      {/* ---------------- Informações + Equipamentos ---------------- */}
      <Grid>
        <Card>
          <CardHeader>
            <CardTitle>
              <FiInfo /> Informações Gerais
            </CardTitle>
          </CardHeader>
          <CardBody>
            <InfoList>
              <InfoItem>
                <dt>
                  <FiCalendar style={{ verticalAlign: "-2px" }} /> Data de
                  Início
                </dt>
                <dd>{new Date(order.start_date).toLocaleDateString("pt-BR")}</dd>
              </InfoItem>
              <InfoItem>
                <dt>
                  <FiUser style={{ verticalAlign: "-2px" }} /> Cliente
                </dt>
                <dd>{order.client?.client_name ?? "—"}</dd>
              </InfoItem>
              <InfoItem>
                <dt>
                  <FiPhone style={{ verticalAlign: "-2px" }} /> Telefone
                </dt>
                <dd>
                  {formatPhone(order.client?.phone?.phone_number) || "—"}
                </dd>
              </InfoItem>
              <InfoItem>
                <dt>
                  <FiCpu style={{ verticalAlign: "-2px" }} /> Equipamento atual
                </dt>
                <dd>
                  {selectedDevice
                    ? selectedDevice.device_name
                    : hasSelection
                    ? selectedDeviceId
                    : "—"}
                </dd>
              </InfoItem>
              <InfoItem>
                <dt>Valor total</dt>
                <dd>
                  <Money>{brl(totalValue)}</Money>
                </dd>
              </InfoItem>
            </InfoList>

            {hasSelection && (
              <div style={{ marginTop: 16, borderTop: "1px solid #2a2f45", paddingTop: 12 }}>
                <CardTitle style={{ fontSize: 13, marginBottom: 8 }}>
                  <FiCamera /> Foto do equipamento
                </CardTitle>

                {photoLoading ? (
                  <span style={{ fontSize: 13, opacity: 0.7 }}>Carregando...</span>
                ) : conditionPhoto?.hasPhoto && conditionPhoto.url ? (
                  <>
                    <img
                      src={conditionPhoto.url}
                      alt="Condição do equipamento"
                      onClick={() => setPhotoViewerOpen(true)}
                      style={{
                        width: 90,
                        height: 45,
                        objectFit: "cover",
                        borderRadius: 8,
                        marginBottom: 8,
                        cursor: "zoom-in"
                      }}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FiEye />}
                        onClick={() => setPhotoViewerOpen(true)}
                      >
                        Visualizar
                      </Button>
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        disabled={photoUploading}
                      >
                        Substituir
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/heic,image/heif"
                          hidden
                          onChange={handlePhotoFileChange}
                        />
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={photoUploading}
                        onClick={handleRemoveConditionPhoto}
                      >
                        Remover
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    disabled={photoUploading}
                  >
                    {photoUploading ? "Enviando..." : "Adicionar foto"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/heic,image/heif"
                      hidden
                      onChange={handlePhotoFileChange}
                    />
                  </Button>
                )}
                <p style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
                  Registra a condição do equipamento no momento do atendimento.
                  A foto é removida automaticamente após 6 meses.
                </p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <FiSmartphone /> Equipamentos
            </CardTitle>
            <CardActions>
              <CustomTooltip title="Listar equipamentos">
                <ActionButton onClick={() => setAssignModalOpen(true)}>
                  <MdOutlinePlaylistAddCheck size={20} />
                </ActionButton>
              </CustomTooltip>
              <CustomTooltip title="Novo equipamento">
                <ActionButton onClick={() => openDeviceModal()}>
                  <IoMdAdd size={20} />
                </ActionButton>
              </CustomTooltip>
              <CustomTooltip title="Mostrar todos os equipamentos">
                <ActionButton onClick={() => selectDeviceForFilter("", "")}>
                  <FaList size={18} />
                </ActionButton>
              </CustomTooltip>
            </CardActions>
          </CardHeader>
          <CardBody>
            {noDevices ? (
              <EmptyState>
                <FiInbox />
                Nenhum equipamento cadastrado nesta ordem.
              </EmptyState>
            ) : (
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Modelo</th>
                      <th>Nº Série</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => {
                      const isSelected =
                        String(d.device_id) === String(selectedDeviceId);
                      return (
                        <TableRow key={d.device_id} $selected={isSelected}>
                          <TableCell>{d.device_name}</TableCell>
                          <TableCell>{d.device_model}</TableCell>
                          <TableCell>{d.device_serial_number}</TableCell>
                          <ActionsCell>
                            {!isUser && (
                              <>
                                <CustomTooltip title="Editar equipamento">
                                  <ActionButton onClick={() => openDeviceModal(d)}>
                                    <FiEdit size={18} color="#34d399" />
                                  </ActionButton>
                                </CustomTooltip>
                                <CustomTooltip title="Excluir equipamento">
                                  <ActionButton onClick={() => askDeleteDevice(d)}>
                                    <FiTrash2 size={18} color="#fb7185" />
                                  </ActionButton>
                                </CustomTooltip>
                              </>
                            )}
                            <CustomTooltip
                              title={
                                isSelected
                                  ? "Equipamento selecionado"
                                  : "Selecionar equipamento"
                              }
                            >
                              <ActionButton
                                onClick={() =>
                                  selectDeviceForFilter(
                                    d.device_id,
                                    d.device_serial_number
                                  )
                                }
                              >
                                <IoCheckmarkDoneSharp
                                  size={20}
                                  color={isSelected ? "#2b7bff" : "#8a8fab"}
                                />
                              </ActionButton>
                            </CustomTooltip>
                          </ActionsCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* ---------------- Guia de seleção ---------------- */}
      {!hasSelection && !noDevices && (
        <StepBanner>
          <FiInfo size={18} />
          <span>
            Selecione um equipamento na tabela acima (ícone{" "}
            <strong>✓</strong>) para gerenciar os <strong>serviços</strong> e as{" "}
            <strong>peças</strong> correspondentes.
          </span>
        </StepBanner>
      )}

      {/* ---------------- Serviços + Peças ---------------- */}
      <Grid2>
        <Card>
          <CardHeader>
            <CardTitle>
              <FiTool /> Serviços
              {selectedDevice && (
                <DeviceTag>{selectedDevice.device_name}</DeviceTag>
              )}
            </CardTitle>
            {hasSelection && (
              <CardActions>
                <CustomTooltip title="Listar serviços">
                  <ActionButton onClick={() => setAssignServiceModalOpen(true)}>
                    <MdOutlinePlaylistAddCheck size={20} />
                  </ActionButton>
                </CustomTooltip>
                <CustomTooltip title="Novo serviço">
                  <ActionButton onClick={openServiceModalCreate}>
                    <IoMdAdd size={20} />
                  </ActionButton>
                </CustomTooltip>
              </CardActions>
            )}
          </CardHeader>
          <CardBody>
            {services.length === 0 ? (
              <EmptyState>
                <FiTool />
                {hasSelection
                  ? "Nenhum serviço para este equipamento."
                  : "Selecione um equipamento para ver os serviços."}
              </EmptyState>
            ) : (
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                      {hasSelection && (
                        <th style={{ textAlign: "right" }}>Ações</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>{s.description}</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          <Money>{brl(s.cost)}</Money>
                        </TableCell>
                        {hasSelection && (
                          <ActionsCell>
                            <CustomTooltip title="Remover serviço da ordem">
                              <ActionButton onClick={() => askRemoveService(s.id)}>
                                <FiTrash2 size={18} color="#fb7185" />
                              </ActionButton>
                            </CustomTooltip>
                            {!isUser && (
                              <CustomTooltip title="Editar serviço">
                                <ActionButton
                                  onClick={() => openServiceModalEdit(s.id)}
                                >
                                  <FiEdit size={18} color="#34d399" />
                                </ActionButton>
                              </CustomTooltip>
                            )}
                            {!isUser && (
                              <CustomTooltip title="Excluir serviço definitivamente">
                                <ActionButton
                                  onClick={() => askDeleteService(s.id)}
                                >
                                  <FiTrash2 size={18} color="#a21413" />
                                </ActionButton>
                              </CustomTooltip>
                            )}
                          </ActionsCell>
                        )}
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <FiCpu /> Peças
              {selectedDevice && (
                <DeviceTag>{selectedDevice.device_name}</DeviceTag>
              )}
            </CardTitle>
            {hasSelection && (
              <CardActions>
                <CustomTooltip title="Listar peças">
                  <ActionButton onClick={() => setAssignProductModalOpen(true)}>
                    <MdOutlinePlaylistAddCheck size={20} />
                  </ActionButton>
                </CustomTooltip>
              </CardActions>
            )}
          </CardHeader>
          <CardBody>
            {products.length === 0 ? (
              <EmptyState>
                <FiCpu />
                {hasSelection
                  ? "Nenhuma peça para este equipamento."
                  : "Selecione um equipamento para ver as peças."}
              </EmptyState>
            ) : (
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>Peça</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                      <th style={{ textAlign: "center" }}>Qtd</th>
                      {hasSelection && (
                        <th style={{ textAlign: "right" }}>Ações</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {products?.map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          <Money>{brl(p.price)}</Money>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {p.quantity}
                        </TableCell>
                        {hasSelection && (
                          <ActionsCell>
                            <CustomTooltip title="Remover peça da ordem">
                              <ActionButton onClick={() => askRemoveProduct(p.id)}>
                                <FiTrash2 size={18} color="#fb7185" />
                              </ActionButton>
                            </CustomTooltip>
                          </ActionsCell>
                        )}
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </CardBody>
        </Card>
      </Grid2>

      {/* ---------------- Resumo fixo ---------------- */}
      <SummaryBar>
        <SummaryItem>
          <span className="label">
            Total Serviços{hasSelection ? " (equipamento)" : ""}
          </span>
          <span className="value">{brl(totalServicesValue)}</span>
        </SummaryItem>
        <SummaryItem>
          <span className="label">
            Total Peças{hasSelection ? " (equipamento)" : ""}
          </span>
          <span className="value">{brl(totalPartsValue)}</span>
        </SummaryItem>
        <SummaryItem $accent={!hasSelection}>
          <span className="label">
            {hasSelection ? "Total do Equipamento" : "Valor Total"}
          </span>
          <span className="value">{brl(totalValue)}</span>
        </SummaryItem>
        {hasSelection && (
          <SummaryItem $accent>
            <span className="label">Total Geral da OS</span>
            <span className="value">{brl(orderGrandTotal)}</span>
          </SummaryItem>
        )}
      </SummaryBar>

      {/* ---------------- Modais ---------------- */}
      <DeviceCreateModal
        open={deviceModalOpen}
        onClose={() => setDeviceModalOpen(false)}
        onDeviceCreated={handleDeviceSaved}
        client_id={order.client.client_id}
        device={deviceToEdit}
        order_id={id!}
      />

      <AssignDeviceModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        onDeviceAssigned={() => {
          api.get(`/work-order/${id}/devices`).then((r) => setDevices(r.data));
          refreshOrder();
        }}
        clientId={order.client.client_id}
        workOrderId={id!}
      />

      <AssignServiceModal
        open={assignServiceModalOpen}
        onClose={() => setAssignServiceModalOpen(false)}
        onServiceAssigned={() => {
          fetchServices(clientId!, serialFilter);
          fetchOrderTotal(clientId!);
        }}
        deviceId={selectedDeviceId ? +selectedDeviceId : undefined}
        workOrderId={id!}
      />

      <AssignProductModal
        open={assignProductModalOpen}
        onClose={() => setAssignProductModalOpen(false)}
        onProductAssigned={() => {
          fetchProducts(clientId!, serialFilter);
          fetchOrderTotal(clientId!);
        }}
        deviceId={selectedDeviceId ? +selectedDeviceId : undefined}
        workOrderId={id!}
      />

      <ServiceCreateModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        onServiceCreated={handleServiceSaved}
        deviceId={selectedDeviceId ? +selectedDeviceId : undefined}
        workOrderId={id!}
        serviceToEdit={serviceToEdit}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Confirmar Exclusão"
        description={`Confirma excluir o equipamento ${deviceToDelete?.device_name}?`}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDeleteDevice}
      />

      <ConfirmDialog
        open={confirmRemoveService}
        title="Confirmar Exclusão"
        description={`Deseja mesmo remover esté serviço?`}
        onClose={() => setConfirmRemoveService(false)}
        onConfirm={handleRemoveService}
      />

      <ConfirmDialog
        open={confirmDeleteService}
        title="Confirmar Exclusão"
        description={`Deseja mesmo deletar esté serviço?`}
        onClose={() => setConfirmDeleteService(false)}
        onConfirm={handleDeleteService}
      />

      <ConfirmDialog
        open={confirmRemoveProduct}
        title="Confirmar Remoção"
        description={`Deseja mesmo remover esté produto?`}
        onClose={() => setConfirmRemoveProduct(false)}
        onConfirm={handleRemoveProduct}
      />

      <Dialog
        open={finalizeModalOpen}
        onClose={() => !finalizing && setFinalizeModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Finalizar Ordem de Serviço</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Total da OS: <strong>{brl(orderGrandTotal)}</strong>
            <br />
            Já recebido: <strong>{brl(amountReceived)}</strong>
            <br />
            Saldo restante: <strong>{brl(remainingBalance)}</strong>
          </DialogContentText>

          <TextField
            fullWidth
            label="Valor a receber agora"
            type="number"
            value={finalizeAmount}
            onChange={(e) => setFinalizeAmount(e.target.value)}
            disabled={finalizing}
            sx={{ mb: 2 }}
            inputProps={{ step: "0.01", min: 0 }}
            helperText="Deixe 0 se o pagamento já foi recebido antes ou não se aplica."
          />

          <FormControl fullWidth sx={{ mb: 2 }} disabled={finalizing}>
            <InputLabel>Forma de pagamento</InputLabel>
            <Select
              label="Forma de pagamento"
              value={finalizePaymentMethod}
              onChange={(e) => setFinalizePaymentMethod(e.target.value as string)}
            >
              {Object.values(FinancialPaymentMethod).map((method) => (
                <MenuItem key={method} value={method}>
                  {FinancialPaymentMethodLabel[method]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Observações (opcional)"
            value={finalizeNotes}
            onChange={(e) => setFinalizeNotes(e.target.value)}
            disabled={finalizing}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeModalOpen(false)} disabled={finalizing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleFinalizeOrder}
            disabled={finalizing}
          >
            {finalizing ? "Finalizando..." : "Confirmar e Finalizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={photoPromptOpen} onClose={() => handlePhotoPromptChoice(false)}>
        <DialogTitle>Gerar com fotos dos equipamentos?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Um ou mais equipamentos desta OS têm foto de condição cadastrada.
            Deseja incluir essas fotos no documento gerado?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handlePhotoPromptChoice(false)}>Sem fotos</Button>
          <Button variant="contained" onClick={() => handlePhotoPromptChoice(true)}>
            Com fotos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoViewerOpen}
        onClose={() => setPhotoViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent
          sx={{
            position: "relative",
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#000"
          }}
        >
          <IconButton
            onClick={() => setPhotoViewerOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" }
            }}
          >
            <FiX />
          </IconButton>
          {conditionPhoto?.url && (
            <img
              src={conditionPhoto.url}
              alt="Condição do equipamento"
              style={{
                width: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
                display: "block"
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default WorkOrderDetail;
